import React from "react";
import { useSearchParams } from "react-router-dom";
import GameCard from "../components/games/GameCard";
import InfoTooltip from "../components/ui/InfoTooltip";
import Button from "../components/ui/Button";
import { getTrendingGames, getHiddenGems, getGreatIndies, getClassicGames, getAvailableGenres } from "../api/games";
import { useAuth } from "../AuthContext";

const CATEGORIES = [
  {
    key: "trending",
    label: "Popular right now",
    icon: "🔥",
    info: "Ranked by a blend of critic and user scores, with a bonus for very recent releases, so both proven hits and buzzy new games can show up here.",
  },
  {
    key: "hidden-gems",
    label: "Hidden gems",
    icon: "💎",
    info: "Strong critic or user scores, but we actively exclude the most mainstream/popular titles, so this stays a place to find something genuinely overlooked.",
  },
  {
    key: "great-indies",
    label: "Great indies",
    icon: "🎮",
    info: "Independently developed games (tagged or categorized as Indie) that also clear a quality bar on critic or user score.",
  },
  {
    key: "classic",
    label: "Classic",
    icon: "⭐",
    info: "A 90+ critic score AND at least 5 years old by default. Pick a different release age below to widen or narrow that.",
  },
];

const PLATFORMS = [
  { value: "", label: "All platforms" },
  { value: "PC", label: "PC" },
  { value: "PlayStation", label: "PlayStation" },
  { value: "Xbox", label: "Xbox" },
  { value: "Nintendo Switch", label: "Nintendo Switch" },
  { value: "Mobile", label: "Mobile" },
];

const RELEASE_AGES = [
  { value: "", label: "Any time" },
  { value: "6m", label: "Last 6 months" },
  { value: "1y", label: "Last year" },
  { value: "3y", label: "Last 3 years" },
  { value: "older", label: "Older / classics" },
];

// A short, curated list rather than the full raw tag set — keeps the filter
// useful instead of overwhelming (per client preference for a small set of filters).
const CURATED_TAGS = [
  { value: "", label: "Any tag" },
  { value: "Open World", label: "Open World" },
  { value: "Story Rich", label: "Story Rich" },
  { value: "Multiplayer", label: "Multiplayer" },
  { value: "Co-op", label: "Co-op" },
  { value: "Singleplayer", label: "Singleplayer" },
  { value: "Roguelike", label: "Roguelike" },
  { value: "Horror", label: "Horror" },
  { value: "Retro", label: "Retro" },
];

const GameCardSkeleton = () => (
  <div className="rounded-3xl border border-white/10 bg-surface-card/60 backdrop-blur-md shadow-card overflow-hidden">
    <div className="h-[200px] gg-shimmer" />
    <div className="p-4 space-y-2.5">
      <div className="h-6 rounded gg-shimmer" />
      <div className="h-4 w-2/3 rounded gg-shimmer" />
      <div className="flex gap-2 mt-2">
        <div className="h-6 w-14 rounded-full gg-shimmer" />
        <div className="h-6 w-20 rounded-full gg-shimmer" />
      </div>
    </div>
  </div>
);

// Consistently styled labelled dropdown for the filter toolbar.
const FilterSelect = ({ label, value, onChange, children }) => (
  <div>
    <label className="block text-[0.62rem] font-bold uppercase tracking-wider text-white/45 mb-1.5">
      {label}
    </label>
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className="w-full h-10 appearance-none rounded-xl pl-3.5 pr-9 text-sm text-white bg-white/[0.05] border border-white/10 outline-none cursor-pointer transition-colors focus:border-brand-purple-light/70 focus:bg-white/[0.08]"
      >
        {children}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-xs">
        ▾
      </span>
    </div>
  </div>
);

export default function GamesPage() {
  const { blacklist } = useAuth();
  const [searchParams] = useSearchParams();
  const [games, setGames] = React.useState([]);
  const [loading, setLoading] = React.useState(true); // first page / filter change
  const [loadingMore, setLoadingMore] = React.useState(false); // "load more"
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [activeFilter, setActiveFilter] = React.useState("trending");
  // Preselect filters from URL query (e.g. tag chips link to /games?tag=...)
  const [platform, setPlatform] = React.useState(searchParams.get("platform") || "");
  const [releaseAge, setReleaseAge] = React.useState(searchParams.get("releaseAge") || "");
  const [genre, setGenre] = React.useState(searchParams.get("genre") || "");
  const [tag, setTag] = React.useState(searchParams.get("tag") || "");
  const [genreOptions, setGenreOptions] = React.useState([]);

  React.useEffect(() => {
    getAvailableGenres().then((genres) => {
      if (Array.isArray(genres) && genres.length > 0) {
        const normalized = genres.map((g) => (typeof g === "string" ? g : g.name)).filter(Boolean);
        setGenreOptions(normalized);
      }
    });
  }, []);

  // Helpers: session-seeded shuffle so browse categories vary per session
  const getSessionSeed = () => {
    try {
      let seed = sessionStorage.getItem("browse_seed");
      if (!seed) {
        seed = Math.floor(Math.random() * 2 ** 31).toString();
        sessionStorage.setItem("browse_seed", seed);
      }
      return Number(seed);
    } catch (e) {
      return Math.floor(Math.random() * 2 ** 31);
    }
  };

  const mulberry32 = (a) => {
    return function () {
      var t = (a += 0x6d2b79f5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const seededShuffle = React.useCallback((arr, seed) => {
    const a = arr.slice();
    const rand = mulberry32(seed >>> 0);
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }, []);

  const hashString = React.useCallback((s) => {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h;
  }, []);

  const fetchGames = React.useCallback(
    (filterType, currentPage, filters) => {
      const isFirstPage = currentPage === 1;
      // First page / filter change shows the full skeleton grid; "load more"
      // only shows a small appended skeleton row + button spinner.
      if (isFirstPage) setLoading(true);
      else setLoadingMore(true);

      let fetchFunction;

      switch (filterType) {
        case "trending":
          fetchFunction = getTrendingGames;
          break;
        case "hidden-gems":
          fetchFunction = getHiddenGems;
          break;
        case "great-indies":
          fetchFunction = getGreatIndies;
          break;
        case "classic":
          fetchFunction = getClassicGames;
          break;
        default:
          fetchFunction = getTrendingGames;
          break;
      }

      fetchFunction(currentPage, 20, blacklist || [], filters)
        .then((res) => {
          const sessionSeed = getSessionSeed();
          const filterKey = `${filterType}:${filters.platform || ""}:${filters.releaseAge || ""}:${filters.genre || ""}:${filters.tag || ""}`;
          const compositeSeed = (sessionSeed ^ hashString(filterKey)) >>> 0;
          const pageGames = Array.isArray(res.games) ? res.games : res.games || [];
          const shuffled = seededShuffle(pageGames, compositeSeed + (currentPage || 0));

          if (isFirstPage) {
            setGames(shuffled);
          } else {
            setGames((prev) => {
              const existing = new Set(prev.map((g) => g.id || g._id));
              const fresh = shuffled.filter((g) => g && !existing.has(g.id || g._id));
              return [...prev, ...fresh];
            });
          }
          setTotalPages(res.pagination?.totalPages || 1);
        })
        .catch(() => {
          if (isFirstPage) setGames([]);
        })
        .finally(() => {
          if (isFirstPage) setLoading(false);
          else setLoadingMore(false);
        });
    },
    [blacklist, seededShuffle, hashString],
  );

  React.useEffect(() => {
    fetchGames(activeFilter, 1, { platform, releaseAge, genre, tag });
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, platform, releaseAge, genre, tag, fetchGames]);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setPage(1);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchGames(activeFilter, nextPage, { platform, releaseAge, genre, tag });
  };

  const activeMeta = CATEGORIES.find((c) => c.key === activeFilter);
  const hasActiveFilters = Boolean(platform || releaseAge || genre || tag);
  const tagInList = CURATED_TAGS.some((t) => t.value === tag);

  const clearAll = () => {
    setPlatform("");
    setReleaseAge("");
    setGenre("");
    setTag("");
  };

  const filterChips = [
    platform && {
      key: "platform",
      label: PLATFORMS.find((p) => p.value === platform)?.label || platform,
      clear: () => setPlatform(""),
    },
    releaseAge && {
      key: "releaseAge",
      label: RELEASE_AGES.find((r) => r.value === releaseAge)?.label || releaseAge,
      clear: () => setReleaseAge(""),
    },
    genre && { key: "genre", label: genre, clear: () => setGenre("") },
    tag && { key: "tag", label: `#${tag}`, clear: () => setTag("") },
  ].filter(Boolean);

  return (
    <div className="min-h-[calc(100vh-var(--header-height))] overflow-x-clip py-6 sm:py-8 md:py-10 px-3 sm:px-6 md:px-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-surface-card/40 backdrop-blur-md mb-7 sm:mb-8 px-6 sm:px-10 py-9 sm:py-12">
          <div className="absolute inset-0 gg-grid-backdrop opacity-40" />
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-56 w-[70%] rounded-full bg-brand-purple/25 blur-3xl animate-pulseGlow" />
          <div className="absolute top-6 right-8 h-24 w-24 rounded-full bg-brand-gold/15 blur-2xl animate-floaty" />
          <div className="relative text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold text-brand-gold-soft backdrop-blur-sm mb-4">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
              Discover your next play
            </span>
            <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl md:text-6xl tracking-tight">
              Browse <span className="gg-gradient-text">Games</span>
            </h1>
            <p className="text-white/65 text-sm sm:text-base mt-3 max-w-2xl mx-auto">
              Trending hits, hidden gems, indie favourites, and timeless classics,
              filtered your way.
            </p>
          </div>
        </div>

        {/* Category selector — visual tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 mb-5">
          {CATEGORIES.map((cat) => {
            const active = activeFilter === cat.key;
            return (
              <div
                key={cat.key}
                role="button"
                tabIndex={0}
                onClick={() => handleFilterChange(cat.key)}
                onKeyDown={(e) => {
                  if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    handleFilterChange(cat.key);
                  }
                }}
                className={[
                  "group flex items-center gap-3 rounded-2xl border px-3.5 sm:px-4 py-3 sm:py-3.5 cursor-pointer select-none transition-all duration-200",
                  active
                    ? "border-transparent bg-brand-gradient shadow-glow-purple -translate-y-0.5"
                    : "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.07] hover:-translate-y-0.5",
                ].join(" ")}
              >
                <span
                  className={[
                    "flex items-center justify-center h-10 w-10 rounded-xl text-xl shrink-0 transition-colors",
                    active ? "bg-white/20" : "bg-white/[0.06]",
                  ].join(" ")}
                >
                  {cat.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={[
                      "block font-heading font-bold text-sm sm:text-[0.95rem] leading-tight truncate",
                      active ? "text-white" : "text-white/85",
                    ].join(" ")}
                  >
                    {cat.label}
                  </span>
                </span>
                <InfoTooltip text={cat.info} className={active ? "text-white/90" : "text-white/45"} />
              </div>
            );
          })}
        </div>

        {/* Filter toolbar */}
        <div className="rounded-3xl border border-white/10 bg-surface-card/40 backdrop-blur-md p-4 sm:p-5 mb-7">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FilterSelect label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
              {PLATFORMS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </FilterSelect>

            <FilterSelect label="Release age" value={releaseAge} onChange={(e) => setReleaseAge(e.target.value)}>
              {RELEASE_AGES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </FilterSelect>

            <FilterSelect label="Genre" value={genre} onChange={(e) => setGenre(e.target.value)}>
              <option value="">All genres</option>
              {genreOptions.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </FilterSelect>

            <FilterSelect label="Tag" value={tag} onChange={(e) => setTag(e.target.value)}>
              {CURATED_TAGS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
              {tag && !tagInList && <option value={tag}>{tag}</option>}
            </FilterSelect>
          </div>

          {filterChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-white/10">
              <span className="text-xs font-semibold uppercase tracking-wide text-white/40">Active</span>
              {filterChips.map((f) => (
                <button
                  key={f.key}
                  onClick={f.clear}
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand-purple/40 bg-brand-purple/15 px-3 py-1 text-xs font-semibold text-white/90 transition-colors hover:border-brand-gold hover:text-brand-gold"
                >
                  {f.label}
                  <span className="text-white/50">✕</span>
                </button>
              ))}
              <button onClick={clearAll} className="ml-1 text-xs font-semibold text-brand-gold hover:underline">
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Results header */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <h2 className="flex items-center gap-3 font-heading font-extrabold text-white text-xl sm:text-2xl">
            <span className="h-7 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
            <span>{activeMeta?.icon}</span>
            {activeMeta?.label || "Games"}
          </h2>
          {!loading && games.length > 0 && (
            <span className="rounded-full px-4 py-1.5 text-sm font-bold text-brand-gold-soft bg-brand-gold/10 border border-brand-gold/30 backdrop-blur-md whitespace-nowrap">
              {games.length} shown
            </span>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {[...Array(10)].map((_, i) => (
              <GameCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {Array.isArray(games) && games.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 animate-fadeIn">
                {games.map((game) => (
                  <GameCard key={game.id || game._id} game={game} />
                ))}
              </div>
            ) : (
              <div className="rounded-3xl border border-white/10 bg-surface-card/40 backdrop-blur-md px-6 py-16 text-center">
                <div className="text-4xl mb-3">🔍</div>
                <p className="text-white/80 font-heading font-bold text-lg">No games found</p>
                <p className="text-white/50 text-sm mt-1">
                  {hasActiveFilters
                    ? "Try removing a filter or two to widen your search."
                    : "Check back soon. New games are added regularly."}
                </p>
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" className="mt-5" onClick={clearAll}>
                    Clear filters
                  </Button>
                )}
              </div>
            )}

            {/* Appended skeletons while loading more */}
            {loadingMore && (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 mt-5">
                {[...Array(5)].map((_, i) => (
                  <GameCardSkeleton key={`more-${i}`} />
                ))}
              </div>
            )}

            <div className="flex justify-center mt-8 sm:mt-10">
              {page < totalPages ? (
                <Button variant="primary" size="lg" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load More Games"}
                </Button>
              ) : (
                games.length > 0 && (
                  <p className="text-white/45 text-sm text-center">
                    ✨ You've reached the end of {activeMeta?.label?.toLowerCase() || "this category"}, {games.length} games in total
                  </p>
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
